import React, {useState, useEffect} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {Grid, Card, Paper, Button, Chip, Divider, IconButton} from '@mui/material';
import {ArrowBack, Edit} from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import {Axios} from 'services/axios';
import {Block} from 'notiflix';

function SectionTemplatePreview() {
  const {id} = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTemplate();
  }, [id]);

  const fetchTemplate = async () => {
    try {
      setLoading(true);
      Block.circle('#preview-container');
      const res = await Axios.fetch(`section-templates/${id}`);
      setTemplate(res.data.data.template);
    } catch (error) {
      console.error('Error fetching template:', error);
    } finally {
      setLoading(false);
      Block.remove('#preview-container');
    }
  };

  if (loading) return null;

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox pt={6} pb={3} id="preview-container">
        <Grid container spacing={3}>
          {/* Header */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    <IconButton onClick={() => navigate('/section-templates')}>
                      <ArrowBack />
                    </IconButton>
                  </Grid>
                  <Grid item xs>
                    <MDTypography variant="h4">{template?.name}</MDTypography>
                    <MDTypography variant="subtitle2" color="text">
                      {template?.typeKey}
                    </MDTypography>
                  </Grid>
                  <Grid item>
                    <MDButton
                      variant="contained"
                      color="primary"
                      startIcon={<Edit />}
                      onClick={() => navigate(`/section-templates/${id}/edit`)}>
                      Edit Template
                    </MDButton>
                  </Grid>
                </Grid>
              </MDBox>

              <Divider />

              {/* Preview Content */}
              <MDBox p={3}>
                <Grid container spacing={3}>
                  {/* Template Info */}
                  <Grid item xs={12}>
                    <Paper sx={{p: 2}}>
                      <MDTypography variant="h6" gutterBottom>
                        Template Information
                      </MDTypography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <MDTypography variant="caption" color="text">
                            Category
                          </MDTypography>
                          <MDTypography variant="body2">
                            <Chip label={template?.category} size="small" />
                          </MDTypography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <MDTypography variant="caption" color="text">
                            Description
                          </MDTypography>
                          <MDTypography variant="body2">
                            {template?.description || 'No description'}
                          </MDTypography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Thumbnail Preview */}
                  {template?.thumbnailUrl && (
                    <Grid item xs={12}>
                      <Paper sx={{p: 2}}>
                        <MDTypography variant="h6" gutterBottom>
                          Thumbnail
                        </MDTypography>
                        <MDBox
                          component="img"
                          src={template.thumbnailUrl}
                          alt={template.name}
                          sx={{
                            maxWidth: '100%',
                            maxHeight: 300,
                            borderRadius: 1,
                            boxShadow: 1,
                          }}
                        />
                      </Paper>
                    </Grid>
                  )}

                  {/* Field Definitions Preview */}
                  <Grid item xs={12}>
                    <Paper sx={{p: 2}}>
                      <MDTypography variant="h6" gutterBottom>
                        Field Definitions (
                        {template?.fieldDefinitions?.length || 0})
                      </MDTypography>
                      <Grid container spacing={2}>
                        {template?.fieldDefinitions?.map((field, index) => (
                          <Grid item xs={12} sm={6} md={4} key={index}>
                            <Card sx={{p: 2}}>
                              <MDTypography variant="body2" fontWeight="bold">
                                {field.label}
                              </MDTypography>
                              <MDTypography
                                variant="caption"
                                color="text"
                                display="block">
                                Key: {field.key}
                              </MDTypography>
                              <Chip
                                label={field.type}
                                size="small"
                                color="info"
                                variant="outlined"
                                sx={{mt: 1}}
                              />
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default SectionTemplatePreview;
